import { Trans } from '@lingui/react/macro';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@repo/videa-components/src/index';
import { ChevronDown } from 'lucide-react';

import type { ImageAnalyses } from '@/@types/latestImagesV2';
import type { GetClaimSummaryResponse } from '@/modules/claims/freeClaims/insuranceExport/types';
import { useLocalizedFormatDate } from '@/modules/claims/freeClaims/state';

import type {
  DateFilterState,
  SelectionModalActions,
  SelectionModalSelectors,
} from '../../../hooks/useImageSelectionModalState';

import { RADIOGRAPH_SECTIONS } from './claimImageSelectorHelpers';
import { OtherSection } from './OtherSection';
import {
  type RadiographSectionItem,
  RadiographsSection,
} from './RadiographsSection';

type VisitDateSectionProps = {
  dateStr: string;
  filters: DateFilterState;
  selectors: SelectionModalSelectors;
  filteredRadiographs: ImageAnalyses[];
  filteredColorImages: ImageAnalyses[];
  claimSummary: GetClaimSummaryResponse | undefined;
  selectedTreatmentIds: string[];
  fmxGrid: (ImageAnalyses | null)[];
  actions: SelectionModalActions;
  viewFilterImageTypes: Set<string>;
};

export const VisitDateSection = ({
  dateStr,
  filters,
  selectors,
  filteredRadiographs,
  filteredColorImages,
  claimSummary,
  selectedTreatmentIds,
  fmxGrid,
  actions,
  viewFilterImageTypes,
}: VisitDateSectionProps) => {
  const localizedFormat = useLocalizedFormatDate();
  const formattedDate = localizedFormat(dateStr, false, 'MMM d, yyyy');
  const showColorImages = selectors
    .presentTypesByDate(dateStr)
    .includes('colorImages');
  const showFmx = selectors.presentTypesByDate(dateStr).includes('fmx');
  const hasRadiographTypes =
    selectors.presentTypesByDate(dateStr).includes('radiographs') || showFmx;
  const viewShowsRadiographs =
    viewFilterImageTypes.has('BITEWING') ||
    viewFilterImageTypes.has('PERIAPICAL') ||
    viewFilterImageTypes.has('PANORAMIC') ||
    viewFilterImageTypes.has('fmx');
  const showRadiographs = hasRadiographTypes && viewShowsRadiographs;
  const showPeriochart = selectors
    .presentTypesByDate(dateStr)
    .includes('periochart');
  const viewShowsOther =
    viewFilterImageTypes.has('colorImages') ||
    viewFilterImageTypes.has('periochart');
  const showOther = (showColorImages || showPeriochart) && viewShowsOther;

  const radiographSections: RadiographSectionItem[] = RADIOGRAPH_SECTIONS.map(
    ({ radiographTypeKey }) => {
      const sectionTitle =
        radiographTypeKey === 'BITEWING' ? (
          <Trans>Bitewings</Trans>
        ) : radiographTypeKey === 'PANORAMIC' ? (
          <Trans>Panoramic</Trans>
        ) : (
          <Trans>Periapicals</Trans>
        );
      return { radiographTypeKey, sectionTitle };
    },
  );

  return (
    <div className="vai-min-w-0">
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="vai-w-full vai-flex vai-items-center vai-justify-between vai-p-4 vai-bg-slate-50 vai-text-sm vai-font-medium vai-text-foreground vai-cursor-pointer hover:vai-bg-slate-200 vai-rounded-t vai-border-b vai-border-slate-200 [&[data-state=open]>svg]:vai-rotate-180">
          {formattedDate}
          <ChevronDown className="vai-h-4 vai-w-4 vai-shrink-0 vai-transition-transform vai-duration-200 vai-text-muted-foreground" />
        </CollapsibleTrigger>
        <CollapsibleContent className="vai-pt-2 vai-pr-4 vai-pb-4 vai-pl-4 vai-overflow-visible vai-flex vai-flex-col">
          {showRadiographs && (
            <RadiographsSection
              radiographSections={radiographSections}
              dateStr={dateStr}
              filters={filters}
              selectors={selectors}
              filteredRadiographs={filteredRadiographs}
              filteredColorImages={filteredColorImages}
              claimSummary={claimSummary}
              selectedTreatmentIds={selectedTreatmentIds}
              fmxGrid={fmxGrid}
              actions={actions}
              viewFilterImageTypes={viewFilterImageTypes}
            />
          )}
          {showOther && (
            <div className={showRadiographs ? 'vai-pt-6' : undefined}>
              <OtherSection
                dateStr={dateStr}
                filters={filters}
                selectors={selectors}
                claimSummary={claimSummary}
                filteredRadiographs={filteredRadiographs}
                filteredColorImages={filteredColorImages}
                selectedTreatmentIds={selectedTreatmentIds}
                actions={actions}
                viewFilterImageTypes={viewFilterImageTypes}
              />
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
